"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface TimezoneContextType {
  timezone: string;
  isLoading: boolean;
  refreshTimezone: () => Promise<void>;
}

const TimezoneContext = createContext<TimezoneContextType>({
  timezone: 'America/New_York',
  isLoading: true,
  refreshTimezone: async () => {}
});

export function TimezoneProvider({ children }: { children: ReactNode }) {
  const [timezone, setTimezone] = useState('America/New_York');
  const [isLoading, setIsLoading] = useState(true);
  const [isClient, setIsClient] = useState(false);

  const fetchTimezone = async () => {
    try {
      const response = await fetch('/api/timezone');
      if (response.ok) {
        const data = await response.json();
        setTimezone(data.timezone);
      }
    } catch (error) {
      console.error('Failed to fetch timezone:', error);
      // Keep default timezone on error
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (isClient) {
      fetchTimezone();
    }
  }, [isClient]);

  const refreshTimezone = async () => {
    setIsLoading(true);
    await fetchTimezone();
  };

  return (
    <TimezoneContext.Provider value={{ timezone, isLoading, refreshTimezone }}>
      {children}
    </TimezoneContext.Provider>
  );
}

export function useTimezone() {
  return useContext(TimezoneContext);
}
